'use client';

import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  Input,
  Table,
  Text,
} from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { FaEye, FaTrash, FaXmark } from 'react-icons/fa6';
import { FaEdit, FaSave } from 'react-icons/fa';
import { CartItemProps } from '@/types/Cart';
import { CartContext } from '@/contexts/CartContext';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FormInputNumber from '@/components/Form/form-input-num';
import { formatCylindricalPower } from '@/utils/formatCylindricalPower';

const schema = z.object({
  product: z
    .string({
      error: 'Producto obrigatório',
    })
    .min(1, { message: 'Producto obrigatório' }),
  product_options: z
    .string({
      error: 'Producto obrigatório1',
    })
    .min(1, { message: 'Producto obrigatório1' }),
  product_type: z
    .string({
      error: 'Producto obrigatório2',
    })
    .min(1, { message: 'Producto obrigatório2' }),
  quantity: z
    .number({ error: 'Quantidade mínima é 1' })
    .min(1, 'Quantidade mínima é 1')
    .max(100, 'Quantidade máxima é 100'),
});

type CartItemPProps = {
  item: CartItemProps;
};

type FormData = z.infer<typeof schema>;

const CartItem = ({ item }: CartItemPProps) => {
  const [itemStage, setItemStage] = useState<'default' | 'edit' | 'delete'>(
    'default',
  );
  const [progress, setProgress] = useState(100);
  const [isRunning, setIsRunning] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const ranOnce = useRef(false);

  const { updateItem, removeItem } = useContext(CartContext);
  const hasDiscount =
    item.discount_percent != null || item.discount_value != null;
  const discountBadge = item.discount_percent != null
    ? `-${item.discount_percent}%`
    : item.discount_value != null
      ? `-${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(item.discount_value)}`
      : null;
  const discountedTotal = hasDiscount ? item.total_price_tax : null;

  const { push } = useRouter();
  const duration = 3000; // tempo total em ms
  const interval = 100; // intervalo de atualização em ms

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      updateItem(
        data.product,
        data.quantity,
        Number(data.product_type),
        data.product_options || 'Sem variaçao',
      );
      setItemStage('default');
    },
    [updateItem],
  );

  const handleTimeout = useCallback(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    if (timerRef.current) clearInterval(timerRef.current);

    const step = (interval / duration) * 100;

    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(timerRef.current!);
          removeItem(item.id);
          setIsRunning(false);
          setProgress(100);
          return 0;
        }
        return prev - step;
      });
    }, interval);
  }, [item.id, removeItem]);

  const handleClick = useCallback(() => {
    setIsRunning(false);
    setProgress(100);
    setItemStage('default');
    ranOnce.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return (
    <Flex
      mb="8px"
      key={item.product_id}
      w="100%"
      pos="relative"
      flexDir="column"
      borderRadius="14px"
      bg={{ base: '#FFFFFF', _dark: '#0C1B2A' }}
      border="2px solid"
      borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}

      transition="border-color 0.2s, box-shadow 0.2s"
      _hover={{ borderColor: 'rgba(0,176,202,0.22)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}
    >
      {/* Main content */}
      <Flex p="12px" gap="12px" align="flex-start">
        {/* Product image */}
        <Flex
          w="72px"
          h="72px"
          borderRadius="10px"
          bg={{ base: '#F8FAFB', _dark: '#0E2030' }}
          border="1px solid"
          borderColor={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.05)' }}
          align="center"
          justify="center"
          flexShrink={0}
          overflow="hidden"
        >
          <Image
            src={item.product_image || '/ido-logo.png'}
            alt={item.product_name}
            w="auto"
            h="60px"
            objectFit="contain"
            p="4px"
          />
        </Flex>

        {/* Info */}
        <Flex flexDir="column" flex="1" minW="0" gap="4px">
          <Text
            fontSize="13px"
            fontWeight="700"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            lineHeight="1.3"
            style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
          >
            {item.product_name}
          </Text>
          {(item.product_type === 2 || item.product_type === 3) && item.product_options_label && (
            <Text fontSize="11px" color={{ base: '#4E6E84', _dark: '#6090A8' }} lineHeight="1.3">
              {item.product_options_label}
            </Text>
          )}
          <Flex justify="space-between" align="center" mt="6px">
            <Flex
              px="7px"
              py="2px"
              borderRadius="full"
              bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.08)' }}
              align="center"
            >
              <Text fontSize="11px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>
                ×{item.quantity}
              </Text>
            </Flex>
            <Flex flexDir="column" align="flex-end" gap="1px">
              {discountedTotal != null && discountBadge && (
                <Flex align="center" gap="5px">
                  <Text fontSize="10px" fontWeight="800" color="#E03030">{discountBadge}</Text>
                  <Text fontSize="11px" fontWeight="500" color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.3)' }} textDecoration="line-through">
                    {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(item.original_total_price_tax!)}
                  </Text>
                </Flex>
              )}
              <Text
                fontSize="15px"
                fontWeight="800"
                color={discountedTotal != null ? '#E03030' : { base: '#0A1B2B', _dark: '#E2EFF5' }}
                letterSpacing="-0.3px"
              >
                {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(discountedTotal ?? item.total_price_tax)}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      {/* Lens config table */}
      {item.product_type === 4 && item.product_options && (() => {
        const options = JSON.parse(item.product_options);
        return (
          <Flex
            width="100%"
            px="12px"
            pb="10px"
            justify="center"
            align="center"
            flexDir="column"
            gap="5px"
          >
            <Text
              w="100%"
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.8px"
              textTransform="uppercase"
              color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.4)' }}
            >
              Lentes de Contacto
            </Text>
            <Table.Root fontSize="11px" width="100%">
              <Table.Body>
                <Table.Row bg={{ base: 'rgba(0,176,202,0.04)', _dark: 'rgba(0,176,202,0.08)' }}>
                  <Table.Cell py="4px" px="6px"></Table.Cell>
                  {options.OE && (
                    <Table.Cell textAlign="center" fontWeight="700" py="4px" px="6px" color="#00B0CA">
                      OE
                    </Table.Cell>
                  )}
                  {options.OD && (
                    <Table.Cell textAlign="center" fontWeight="700" py="4px" px="6px" color="#00B0CA">
                      OD
                    </Table.Cell>
                  )}
                </Table.Row>
                {options.color && (
                  <Table.Row>
                    <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Cor</Table.Cell>
                    <Table.Cell
                      colSpan={(options.OE ? 1 : 0) + (options.OD ? 1 : 0)}
                      py="4px"
                      px="6px"
                      textAlign="center"
                      fontWeight="500"
                    >
                      {options.color}
                    </Table.Cell>
                  </Table.Row>
                )}
                <Table.Row>
                  <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Curva Base</Table.Cell>
                  {options.OE?.base_curve && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OE.base_curve}</Table.Cell>}
                  {options.OD?.base_curve && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OD.base_curve}</Table.Cell>}
                </Table.Row>
                <Table.Row bg={{ base: 'rgba(0,0,0,0.02)', _dark: 'rgba(255,255,255,0.02)' }}>
                  <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Diâmetro</Table.Cell>
                  {options.OE?.diameter && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OE.diameter}</Table.Cell>}
                  {options.OD?.diameter && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OD.diameter}</Table.Cell>}
                </Table.Row>
                {(options.OE?.spherical_power || options.OD?.spherical_power) && (
                  <Table.Row>
                    <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Pot. Esférica</Table.Cell>
                    {options.OE?.spherical_power && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OE.spherical_power}</Table.Cell>}
                    {options.OD?.spherical_power && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OD.spherical_power}</Table.Cell>}
                  </Table.Row>
                )}
                {(options.OE?.cylindrical_power || options.OD?.cylindrical_power) && (
                  <Table.Row bg={{ base: 'rgba(0,0,0,0.02)', _dark: 'rgba(255,255,255,0.02)' }}>
                    <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Pot. Cilíndrica</Table.Cell>
                    {options.OE?.cylindrical_power && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{formatCylindricalPower(options.OE.cylindrical_power)}</Table.Cell>}
                    {options.OD?.cylindrical_power && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{formatCylindricalPower(options.OD.cylindrical_power)}</Table.Cell>}
                  </Table.Row>
                )}
                {(options.OE?.axle || options.OD?.axle) && (
                  <Table.Row>
                    <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Eixo</Table.Cell>
                    {options.OE?.axle && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OE.axle}</Table.Cell>}
                    {options.OD?.axle && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OD.axle}</Table.Cell>}
                  </Table.Row>
                )}
                {(options.OE?.addition || options.OD?.addition) && (
                  <Table.Row bg={{ base: 'rgba(0,0,0,0.02)', _dark: 'rgba(255,255,255,0.02)' }}>
                    <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Adição</Table.Cell>
                    {options.OE?.addition && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OE.addition}</Table.Cell>}
                    {options.OD?.addition && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="500">{options.OD.addition}</Table.Cell>}
                  </Table.Row>
                )}
                <Table.Row bg={{ base: 'rgba(0,0,0,0.03)', _dark: 'rgba(255,255,255,0.04)' }}>
                  <Table.Cell py="4px" px="6px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>Quantidade</Table.Cell>
                  {options.OD && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="600">{item.quantity}</Table.Cell>}
                  {options.OE && <Table.Cell py="4px" px="6px" textAlign="center" fontWeight="600">{item.quantity}</Table.Cell>}
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Flex>
        );
      })()}

      {/* Separator */}
      <Box h="1px" bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.05)' }} mx="12px" />

      {/* Action bar */}
      {itemStage === 'default' && (
        <Flex px="12px" py="8px" gap="6px" justify="flex-end" align="center">
          <IconButton
            size="xs"
            w="28px"
            h="28px"
            minW="28px"
            borderRadius="8px"
            bg="transparent"
            border="1px solid"
            borderColor={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.1)' }}
            color={{ base: '#4E6E84', _dark: '#6090A8' }}
            transition="all 0.18s"
            _hover={{ bg: 'rgba(0,176,202,0.08)', borderColor: 'rgba(0,176,202,0.3)', color: '#00B0CA' }}
            onClick={() => push(`/produto/${item.product_slug}`)}
            aria-label="Ver produto"
          >
            <FaEye />
          </IconButton>
          <IconButton
            size="xs"
            w="28px"
            h="28px"
            minW="28px"
            borderRadius="8px"
            bg="transparent"
            border="1px solid"
            borderColor={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.1)' }}
            color={{ base: '#4E6E84', _dark: '#6090A8' }}
            transition="all 0.18s"
            _hover={{ bg: 'rgba(0,176,202,0.08)', borderColor: 'rgba(0,176,202,0.3)', color: '#00B0CA' }}
            onClick={() => setItemStage('edit')}
            aria-label="Editar quantidade"
          >
            <FaEdit />
          </IconButton>
          <IconButton
            size="xs"
            w="28px"
            h="28px"
            minW="28px"
            borderRadius="8px"
            bg="transparent"
            border="1px solid"
            borderColor={{ base: 'rgba(220,53,69,0.2)', _dark: 'rgba(220,53,69,0.25)' }}
            color={{ base: 'rgba(220,53,69,0.7)', _dark: 'rgba(220,53,69,0.8)' }}
            transition="all 0.18s"
            _hover={{ bg: 'rgba(220,53,69,0.08)', borderColor: 'rgba(220,53,69,0.5)', color: 'rgb(220,53,69)' }}
            onClick={() => { setItemStage('delete'); setIsRunning(true); handleTimeout(); }}
            aria-label="Remover item"
          >
            <FaTrash />
          </IconButton>
        </Flex>
      )}

      {/* Edit mode */}
      {itemStage === 'edit' && (
        <Flex
          px="12px"
          py="10px"
          gap="10px"
          align="center"
          justify="space-between"
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          bg={{ base: 'rgba(0,176,202,0.03)', _dark: 'rgba(0,176,202,0.06)' }}
          borderTop="1px solid"
          borderColor={{ base: 'rgba(0,176,202,0.12)', _dark: 'rgba(0,176,202,0.18)' }}
        >
          <Input type="hidden" {...register('product')} value={item.product_id} />
          <Input type="hidden" {...register('product_options')} value={item.product_options} />
          <Input type="hidden" {...register('product_type')} value={item.product_type} />

          <Text
            fontSize="11px"
            fontWeight="700"
            letterSpacing="0.8px"
            textTransform="uppercase"
            color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.4)' }}
          >
            Quantidade
          </Text>

          <Flex align="center" gap="0">
            <Flex w="90px">
              <Controller
                control={control}
                name="quantity"
                defaultValue={item.quantity}
                render={({ field }) => (
                  <FormInputNumber
                    textAlign="center"
                    step={1}
                    min={1}
                    max={100}
                    value={String(field.value)}
                    defaultValue={String(field.value)}
                    error={errors.quantity?.message}
                    width="100%"
                    border="0px"
                    onValueChange={({ value }) => field.onChange(Number(value))}
                  />
                )}
              />
            </Flex>
            {/* Divider */}
            <Box
              w="1px"
              h="20px"
              bg={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.1)' }}
              mx="8px"
              flexShrink={0}
            />
            <Flex gap="6px">
              <IconButton
                type="submit"
                size="xs"
                w="28px"
                h="28px"
                minW="28px"
                borderRadius="8px"
                bg="#00B0CA"
                color="#fff"
                border="none"
                _hover={{ bg: '#009AB8' }}
                aria-label="Guardar"
              >
                <FaSave />
              </IconButton>
              <IconButton
                size="xs"
                w="28px"
                h="28px"
                minW="28px"
                borderRadius="8px"
                bg="transparent"
                border="1px solid"
                borderColor={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.1)' }}
                color={{ base: '#4E6E84', _dark: '#6090A8' }}
                transition="all 0.18s"
                _hover={{ bg: 'rgba(0,0,0,0.05)', borderColor: 'rgba(0,0,0,0.2)' }}
                onClick={() => setItemStage('default')}
                aria-label="Cancelar"
              >
                <FaXmark />
              </IconButton>
            </Flex>
          </Flex>
        </Flex>
      )}

      {/* Delete countdown overlay */}
      {isRunning && (
        <Flex
          w="100%"
          h="100%"
          bg="rgba(210,38,52,0.94)"
          top="0"
          left="0"
          pos="absolute"
          flexDir="column"
          align="center"
          justify="center"
          gap="10px"
          cursor="pointer"
          onClick={handleClick}
          borderRadius="12px"
          backdropFilter="blur(6px)"
          overflow="hidden"
        >
          {/* Icon badge */}
          <Flex
            w="36px"
            h="36px"
            borderRadius="10px"
            bg="rgba(255,255,255,0.15)"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon w="15px" h="15px" color="#fff">
              <FaTrash />
            </Icon>
          </Flex>

          {/* Labels */}
          <Box textAlign="center">
            <Text
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.8px"
              textTransform="uppercase"
              color="rgba(255,255,255,0.65)"
            >
              A remover
            </Text>
            <Text fontSize="12px" fontWeight="500" color="rgba(255,255,255,0.9)" mt="2px">
              Clique para cancelar
            </Text>
          </Box>

          {/* Full-width countdown bar anchored to bottom */}
          <Box pos="absolute" bottom="0" left="0" w="100%" h="4px" bg="rgba(255,255,255,0.15)">
            <Box
              w={`${progress}%`}
              h="4px"
              bg="white"
              transition="width 0.1s linear"
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

export default CartItem;
